import React, { useEffect, useRef, useState } from "react";

import { useTranslation } from "react-i18next";
import {
  getSystemLanguageCode,
  isMetricRegion,
} from "~/js/utilities/regionAndUnits";

const PROFILE_CONFIG = {
  step: 76.2,
  age: 53,
  height: 176,
  weight: 98,
  gender: "male",
  hrMax: 182,
};

const languageToLocaleMap = {
  en: "en-US",
  de: "de-DE",
  es: "es-ES",
  fr: "fr-FR",
  // Add more language-to-locale mappings as needed
};

const SettingsContext = React.createContext();
export default SettingsContext;

export const SettingsProvider = (props) => {
  const getValidLanguageCode = (languageCode) => {
    const supportedLanguages = i18n.options.languages;
    const fallbackLanguage = i18n.options.fallbackLng;

    if (supportedLanguages.some((language) => language.id === languageCode)) {
      return languageCode;
    } else {
      return fallbackLanguage[0];
    }
  };

  const { t, i18n } = useTranslation();

  const [profile, setProfile] = useState(null);
  const [units, setUnits] = useState(
    JSON.parse(localStorage.getItem("units")) || {
      auto: true,
      metric: isMetricRegion(),
    }
  );
  const previousUnits = useRef(units); // useRef to store the previous state

  const [regional, setRegional] = useState(
    JSON.parse(localStorage.getItem("regional")) || {
      countrycode: navigator.language,
      auto: true,
      language: getValidLanguageCode(getSystemLanguageCode()),
      systemLanguage: getValidLanguageCode(getSystemLanguageCode()),
    }
  );
  const previousRegional = useRef(regional); // useRef to store the previous state

  useEffect(() => {

    let storeRegional = JSON.parse(localStorage.getItem("regional"));
    let systemLanguage = getValidLanguageCode(getSystemLanguageCode());

    if (regional !== null && regional !== previousRegional.current) {

      let newLang = regional.language;
      if (regional.auto) {
        newLang = systemLanguage;
      }
      i18n.changeLanguage(newLang);
      localStorage.setItem(
        "regional",
        JSON.stringify({ ...regional, language: newLang })
      );
    } else if (
      storeRegional !== null &&
      storeRegional !== previousRegional.current
    ) {
      let newLang = storeRegional.language;
      if (storeRegional.auto) {
        newLang = systemLanguage;
      }
      i18n.changeLanguage(newLang);
      setRegional({
        ...storeRegional,
        language: newLang,
        systemLanguage: systemLanguage,
        countrycode: languageToLocaleMap[newLang] || "en-US",
      });
    } else {
      localStorage.setItem("regional", JSON.stringify(regional));
    }
  }, [regional]);

  useEffect(() => {
    let storeUnits = JSON.parse(localStorage.getItem("units"));
    let systemMetric = isMetricRegion();

    if (units !== null && units !== previousUnits.current) {
      let newMetric = units.metric;

      if (units.auto && units.metric !== isMetricRegion()) {
        newMetric = systemMetric;
        setUnits({ auto: true, metric: systemMetric });
      }
      localStorage.setItem(
        "units",
        JSON.stringify({ auto: units.auto, metric: newMetric })
      );
    } else if (storeUnits !== null && storeUnits !== previousUnits.current) {
      let newMetric = storeUnits.metric;
      if (storeUnits.auto) {
        newMetric = systemMetric;
      }
      setUnits({ ...storeUnits, metric: newMetric });
    }
  }, [units]);

  useEffect(() => {
    if (profile === null) {
      if (localStorage.getItem("configProfile") !== null) {
        setProfile(JSON.parse(localStorage.getItem("configProfile")));
      } else {
        localStorage.setItem("configProfile", JSON.stringify(PROFILE_CONFIG));
        setProfile(PROFILE_CONFIG);
      }
    } else if (JSON.parse(localStorage.getItem("configProfile")) !== profile) {
      localStorage.setItem("configProfile", JSON.stringify(profile));
    }
  }, [profile]);

  return (
    <SettingsContext.Provider
      value={{
        profile,
        setProfile,
        units,
        setUnits,
        regional,
        setRegional,
      }}
    >
      {props.children}
    </SettingsContext.Provider>
  );
};
